import { z } from 'zod';
import { SessionListItemSchema } from './auth';
import { AccountStatusSchema } from './people';

/**
 * Phase 1 M5: People › Person › Login & security, the login activity screen, the audit log
 * viewer and the site "suggested networks" helper (spec §3.3–3.6, §5 screens 2 and 7, §8).
 */

export const LoginOutcomeSchema = z.enum([
  'ok',
  'bad_password',
  'locked',
  'disabled',
  'unknown',
  'rate_limited',
  'off_site',
]);
export type LoginOutcome = z.infer<typeof LoginOutcomeSchema>;

export const IdentifierKindSchema = z.enum(['email', 'staff_number', 'legacy_username', 'unknown']);

/** One sign-in attempt. Unknown identifiers are never shown: only their outcome. */
export const LoginEventSchema = z.object({
  id: z.number().int(),
  at: z.iso.datetime(),
  outcome: LoginOutcomeSchema,
  identifierKind: IdentifierKindSchema,
  ip: z.string().nullable(),
  /** The site whose network the IP matched, if any. */
  siteCode: z.string().nullable(),
  siteName: z.string().nullable(),
  /** "Chrome on Windows", from the user agent; never the raw string. */
  deviceLabel: z.string().nullable(),
  /** The account's person, when the identifier matched one. */
  person: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export type LoginEvent = z.infer<typeof LoginEventSchema>;

export const AdminSessionSchema = SessionListItemSchema.omit({ current: true });
export type AdminSession = z.infer<typeof AdminSessionSchema>;

/** What People › Login & security shows for a person. */
export const PersonLoginSchema = z.object({
  personId: z.uuid(),
  /** null = no login. */
  account: z
    .object({
      id: z.uuid(),
      status: AccountStatusSchema,
      /** A temporary lock's end; null while locked by an admin or imported locked. */
      lockedUntil: z.iso.datetime().nullable(),
      loginEmail: z.string().nullable(),
      emailVerified: z.boolean(),
      lastLoginAt: z.iso.datetime().nullable(),
      passwordChangedAt: z.iso.datetime().nullable(),
      mustChangePassword: z.boolean(),
      /** Live manager codes right now (so the panel can say one is already out). */
      liveResetCodes: z.number().int().nonnegative(),
    })
    .nullable(),
  identifiers: z.object({
    staffNumber: z.string().nullable(),
    legacyUsernames: z.array(z.string()),
  }),
  /** What the caller may do here; the API enforces each on its own route too. */
  can: z.object({
    manage: z.boolean(),
    issueResetCode: z.boolean(),
    /** Why a code can't be issued, when it can't (shown as a hint). */
    issueResetCodeReason: z.enum(['self', 'not_in_reporting_line', 'no_permission']).nullable(),
  }),
  /** Live sessions; empty unless the caller can manage. */
  sessions: z.array(AdminSessionSchema),
  /** The last 20 attempts; empty unless the caller can manage. */
  events: z.array(LoginEventSchema),
});
export type PersonLogin = z.infer<typeof PersonLoginSchema>;

export const AdminSessionsResponseSchema = z.object({ sessions: z.array(AdminSessionSchema) });

export const AccountPatchSchema = z.object({
  /** disabled revokes every session; active re-enables (the lock is left as it was). */
  status: z.enum(['active', 'disabled']),
});
export type AccountPatch = z.infer<typeof AccountPatchSchema>;

export const AccountStatusResponseSchema = z.object({
  status: z.enum(['active', 'disabled']),
  sessionsRevoked: z.number().int().nonnegative(),
});
export type AccountStatusResponse = z.infer<typeof AccountStatusResponseSchema>;

/** The code, shown once. Never stored or logged in clear. */
export const ResetCodeIssuedSchema = z.object({
  code: z.string().length(6),
  expiresAt: z.iso.datetime(),
  /** The person was emailed that a code was issued (never the code). */
  notified: z.boolean(),
});
export type ResetCodeIssued = z.infer<typeof ResetCodeIssuedSchema>;

export const ResetEmailSentSchema = z.object({
  sent: z.boolean(),
  reason: z.enum(['disabled', 'no_verified_email']).nullable(),
});
export type ResetEmailSent = z.infer<typeof ResetEmailSentSchema>;

// Login activity (company-wide) ----------------------------------------------

export const LoginEventsQuerySchema = z.object({
  outcome: LoginOutcomeSchema.optional(),
  /** Site code the event matched. `none` = off any known network. */
  site: z.string().trim().max(32).optional(),
  /** Person id. */
  person: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Keyset cursor: ids below this. */
  before: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type LoginEventsQuery = z.infer<typeof LoginEventsQuerySchema>;

export const LoginEventsResponseSchema = z.object({
  events: z.array(LoginEventSchema),
  /** Pass as `before` for the next page; null at the end. */
  nextBefore: z.number().int().nullable(),
});
export type LoginEventsResponse = z.infer<typeof LoginEventsResponseSchema>;

// Audit log ------------------------------------------------------------------

export const AuditEventSchema = z.object({
  id: z.number().int(),
  at: z.iso.datetime(),
  action: z.string(),
  actor: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** View as: the person the actor was viewing as. */
  actingAs: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  subjectType: z.string(),
  subjectId: z.string(),
  /** The person behind the subject (a person, or an account's owner), when known. */
  subject: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  ip: z.string().nullable(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const AuditQuerySchema = z.object({
  /** Actor person id. */
  actor: z.uuid().optional(),
  /** Subject: a person id (matches the person and their account) or any subject id. */
  subject: z.string().trim().max(64).optional(),
  /** Exact action, or a prefix ending in `.` (e.g. `password.`). */
  action: z.string().trim().max(64).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /**
   * Include one row per page viewed in View as (`view_as.request`). Off by default: a View as
   * session is its start and end rows unless someone asks for the pages, or filters on them.
   */
  pageViews: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
    .optional(),
  before: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditResponseSchema = z.object({
  events: z.array(AuditEventSchema),
  nextBefore: z.number().int().nullable(),
});
export type AuditResponse = z.infer<typeof AuditResponseSchema>;

// Suggested networks (site drawer) --------------------------------------------

export const NetworkSuggestionSchema = z.object({
  ip: z.string(),
  /** Sign-in attempts from this address by people whose home site this is, last 30 days. */
  count: z.number().int().positive(),
  people: z.number().int().positive(),
  firstSeen: z.iso.datetime(),
  lastSeen: z.iso.datetime(),
  /** Loopback / RFC 1918 / link-local: not a branch's public address. */
  private: z.boolean(),
  /** Already inside one of the site's ranges. */
  covered: z.boolean(),
});
export type NetworkSuggestion = z.infer<typeof NetworkSuggestionSchema>;

export const NetworkSuggestionsResponseSchema = z.object({
  suggestions: z.array(NetworkSuggestionSchema),
  days: z.number().int().positive(),
});
export type NetworkSuggestionsResponse = z.infer<typeof NetworkSuggestionsResponseSchema>;
